import config from '../config';
import Cookies from 'js-cookie';

// Cache configuration - Optimized for maximum performance
const CACHE_CONFIG = {
  SEARCH_TTL: 10 * 60 * 1000, // 10 minutes (optimized balance)
  SUGGESTIONS_TTL: 20 * 60 * 1000, // 20 minutes
  MAX_CACHE_SIZE: 500, // Increased for better cache hit rate
  INSTANT_CACHE_TTL: 2 * 60 * 1000, // 2 minutes for instant results
};

class SearchService {
  constructor() {
    this.cache = new Map();
    this.instantCache = new Map(); // Cache for instant/typing results
    this.requestQueue = new Map();
    this.searchHistory = this.loadSearchHistory();
  }

  // Generate cache key
  generateCacheKey(endpoint, params) {
    const sortedParams = Object.keys(params)
      .sort()
      .map(key => `${key}=${params[key]}`)
      .join('&');
    return `${endpoint}?${sortedParams}`;
  }

  // Set cache with TTL
  setCache(key, data, ttl = CACHE_CONFIG.SEARCH_TTL) {
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      ttl
    });

    // Clean up old entries if cache is too large
    if (this.cache.size > CACHE_CONFIG.MAX_CACHE_SIZE) {
      const entries = Array.from(this.cache.entries());
      entries.sort(([,a], [,b]) => a.timestamp - b.timestamp);
      const toDelete = entries.slice(0, entries.length - CACHE_CONFIG.MAX_CACHE_SIZE);
      toDelete.forEach(([key]) => this.cache.delete(key));
    }
  }

  // Get cache if not expired
  getCache(key) {
    const cached = this.cache.get(key);
    if (!cached) return null;

    if (Date.now() - cached.timestamp > cached.ttl) {
      this.cache.delete(key);
      return null;
    }

    return cached.data;
  }

  // Load search history from localStorage
  loadSearchHistory() {
    try {
      const history = localStorage.getItem('wennze_search_history');
      return history ? JSON.parse(history) : [];
    } catch (error) {
      console.error('Failed to load search history:', error);
      return [];
    }
  }

  // Save search history to localStorage
  saveSearchHistory() {
    try {
      localStorage.setItem('wennze_search_history', JSON.stringify(this.searchHistory));
    } catch (error) {
      console.error('Failed to save search history:', error);
    }
  }

  // Add search query to history
  addToSearchHistory(query) {
    if (!query || query.trim().length < 2) return;

    const trimmedQuery = query.trim();
    
    // Remove if already exists
    this.searchHistory = this.searchHistory.filter(item => item !== trimmedQuery);
    
    // Add to beginning
    this.searchHistory.unshift(trimmedQuery);
    
    // Keep only last 10 searches
    this.searchHistory = this.searchHistory.slice(0, 10);
    
    this.saveSearchHistory();
  }

  // Get search suggestions based on query
  generateSearchSuggestions(query) {
    if (!query || query.trim().length < 1) return [];

    const lowerQuery = query.toLowerCase().trim();
    const suggestions = [];

    // Add matching items from search history
    const historyMatches = this.searchHistory.filter(item =>
      item.toLowerCase().includes(lowerQuery)
    );
    suggestions.push(...historyMatches);

    // Add smart suggestions
    if (lowerQuery.length >= 2) {
      const smartSuggestions = [
        `${lowerQuery} neuf`,
        `${lowerQuery} occasion`,
        `${lowerQuery} pas cher`,
        `${lowerQuery} haute qualité`,
      ];
      suggestions.push(...smartSuggestions);
    }

    // Remove duplicates and limit to 8 suggestions
    return [...new Set(suggestions)].slice(0, 8);
  }

  // Search products with caching - Updated to match app approach
  async searchProducts(searchQuery, options = {}) {
    if (!searchQuery || searchQuery.trim().length < 2) {
      return {
        products: [],
        hasMore: false,
        total: 0,
        page: options.page || 1,
        fromCache: false
      };
    }

    // Construct params exactly like the app does
    const params = {
      search: searchQuery.trim(),
      page: options.page || 1,
      page_size: options.pageSize || 30,
    };

    // Only add category if it exists and is not empty/0
    if (options.category && options.category !== '0' && options.category !== 0) {
      params.category = options.category;
    }

    const cacheKey = this.generateCacheKey('products/search', params);

    // Check cache first (only use cache for first page or when specifically requested)
    const useCache = options.useCache !== false;
    if (useCache && params.page === 1) { // Only use cache for first page to avoid pagination issues
      const cachedResult = this.getCache(cacheKey);
      if (cachedResult) {

        return { ...cachedResult, fromCache: true };
      }
    }

    // Check if request is already in progress (but allow different pages)
    const requestKey = `${cacheKey}_${Date.now()}`; // Make request keys unique to prevent conflicts
    if (this.requestQueue.has(cacheKey) && params.page === 1) {
      return this.requestQueue.get(cacheKey);
    }
    
    // Make API request with abort signal support
    const requestPromise = this.makeSearchRequest(params, options.signal);
    if (params.page === 1) {
      this.requestQueue.set(cacheKey, requestPromise);
    }

    try {
      const result = await requestPromise;
      
      // Only cache successful results with data (and only first page)
      if (result.products && result.products.length > 0 && params.page === 1) {
        this.setCache(cacheKey, result);
      }
      
      // Add to search history if it's the first page
      if (params.page === 1 && searchQuery.trim()) {
        this.addToSearchHistory(searchQuery);
      }
      
      return { ...result, fromCache: false };
    } catch (error) {
      if (error.name === 'AbortError') {

        throw error;
      }
      console.error('[SearchService] Search error:', error, { params, cacheKey });
      throw error;
    } finally {
      if (params.page === 1) {
        this.requestQueue.delete(cacheKey);
      }
    }
  }

  // Make search API request - Updated to match app implementation exactly
  async makeSearchRequest(params, signal) {
    const startTime = Date.now();

    try {
      if (!params.search || !params.search.trim()) {
        return { products: [], hasMore: false, total: 0, page: params.page };
      }

      const url = `${config.backendUrl}/products/search/`;
      const urlParams = new URLSearchParams();
      
      urlParams.append('search', params.search.trim());
      urlParams.append('page', params.page.toString());
      urlParams.append('page_size', params.page_size.toString());
      
      if (params.category) {
        urlParams.append('category', params.category);
      }

      // Get token from cookies (matching app's token approach)
      const token = document.cookie
        .split('; ')
        .find(row => row.startsWith('authToken='))
        ?.split('=')[1];

      const headers = {
        'Content-Type': 'application/json',
      };

      if (token) {
        headers['Authorization'] = `Token ${token}`;
      }

      const fetchOptions = {
        method: 'GET',
        headers,
      };

      // Add abort signal if provided
      if (signal) {
        fetchOptions.signal = signal;
      }

      const response = await fetch(`${url}?${urlParams.toString()}`, fetchOptions);

      // Check if request was aborted
      if (signal && signal.aborted) {
        throw new DOMException('Request aborted', 'AbortError');
      }

      if (!response.ok) {
        throw new Error(`Search failed: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();

      const products = data.results || [];
      
      // Add display position for analytics tracking
      const productsWithPosition = products.map((product, index) => ({
        ...product,
        searchPosition: (params.page - 1) * params.page_size + index + 1
      }));
      
      return {
        products: productsWithPosition,
        hasMore: data.next !== null,
        total: data.count || 0,
        page: params.page,
      };
    } catch (error) {
      if (error.name === 'AbortError') {

        throw error;
      }
      
      console.error('[SearchService] Search request failed (app-style):', {
        error: error.message,
        params,
        loadTime: Date.now() - startTime,
        timestamp: new Date().toISOString()
      });
      throw error;
    }
  }

  // Get suggested products
  async getSuggestedProducts(page = 1) {
    const cacheKey = this.generateCacheKey('products/suggestions', { page });
    
    // Check cache first
    const cachedResult = this.getCache(cacheKey);
    if (cachedResult) {
      return { ...cachedResult, fromCache: true };
    }

    try {
      const params = new URLSearchParams({ page: page.toString() });
      const response = await fetch(`${config.backendUrl}/products/suggestions/?${params}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const result = {
        products: data.results || [],
        hasMore: data.next !== null,
        total: data.count || 0,
        page,
      };

      this.setCache(cacheKey, result, CACHE_CONFIG.SUGGESTIONS_TTL);
      return { ...result, fromCache: false };
    } catch (error) {
      throw new Error('Erreur lors du chargement des suggestions.');
    }
  }

  // Clear all cache
  clearCache() {
    this.cache.clear();
    this.requestQueue.clear();
  }

  // Clear search history
  clearSearchHistory() {
    this.searchHistory = [];
    this.saveSearchHistory();
  }

  // Get search history
  getSearchHistory() {
    return [...this.searchHistory];
  }
}

// Create singleton instance
const searchService = new SearchService();

export default searchService;
